import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { User, Save, Mail, Phone } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminProfile() {
  const { token, user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    nome: '',
    cognome: '',
    email: '',
    telefono: ''
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/admin/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
    } catch (error) {
      toast.error('Errore caricamento profilo');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/profile`, {
        nome: profile.nome,
        cognome: profile.cognome,
        telefono: profile.telefono
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Profilo aggiornato!');
      
      // Refresh user data in AuthContext to update sidebar
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore aggiornamento profilo');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Profilo">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C5A059]"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Profilo">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="font-cinzel text-2xl md:text-3xl text-[#1A202C] mb-2 flex items-center gap-3">
            <User className="w-8 h-8 text-[#C5A059]" />
            Il Mio Profilo
          </h1>
          <p className="text-[#4A5568]">
            Modifica le informazioni del tuo account amministratore
          </p>
        </div>

        <Card className="border-[#E2E8F0]">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <User className="w-5 h-5 text-[#C5A059]" />
              Dati Personali
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome</Label>
                <Input
                  id="nome"
                  value={profile.nome || ''}
                  onChange={(e) => setProfile({ ...profile, nome: e.target.value })}
                  placeholder="Il tuo nome"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cognome">Cognome</Label>
                <Input
                  id="cognome"
                  value={profile.cognome || ''}
                  onChange={(e) => setProfile({ ...profile, cognome: e.target.value })}
                  placeholder="Il tuo cognome"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email
              </Label>
              <Input
                id="email"
                value={profile.email || ''}
                disabled
                className="bg-gray-50"
              />
              <p className="text-xs text-gray-500">L&apos;email non può essere modificata</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="telefono" className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Telefono
              </Label>
              <Input
                id="telefono"
                value={profile.telefono || ''}
                onChange={(e) => setProfile({ ...profile, telefono: e.target.value })}
                placeholder="+39 333 1234567"
              />
            </div>

            <div className="pt-4 border-t">
              <Button 
                onClick={handleSave}
                disabled={saving}
                className="bg-[#C5A059] hover:bg-[#B08A3E] text-white"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Salvataggio...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Salva Modifiche
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Info */}
        <Card className="border-[#E2E8F0] bg-blue-50">
          <CardContent className="py-4">
            <p className="text-sm text-blue-800">
              <strong>Nota:</strong> Dopo aver modificato il nome, potrebbe essere necessario 
              effettuare il logout e rientrare per vedere le modifiche nella sidebar.
            </p>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
